import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Avatar, AvatarImage } from "../../components/ui/avatar";
import ScheduleGeneratorDrawer from "../../components/schedules/ScheduleGeneratorDrawer";
import SchedulePreviewModal from "../../components/schedules/SchedulePreviewModal";
import { Asset, ScheduleConfig, ScheduleLine, UpfrontCharges } from "../../types/schema";
import { computeAssetValueTotals } from "../../lib/calculations";
import { Schedule } from "../../store/appStore";
import apiService from "../../services/apiService";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Badge } from "../../components/ui/badge";
import { formatDisplayDate } from "../../lib/dateFormat";
import { Eye, Trash2, Loader2 } from "lucide-react";
import astrafinLogo from "../../assets/logo_two.svg";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";



interface RentalSchedulesProps {
  onViewSchedule: (id: string) => void;
  tdsRateList: any[];
}

function isAssignLaterSchedule(schedule: Schedule & {
  assignMode?: string;
  lenderAssignment?: string;
  assignmentMode?: string;
}): boolean {
  const mode = String(
    schedule.assignMode ?? schedule.lenderAssignment ?? schedule.assignmentMode ?? ""
  ).toLowerCase();
  return mode === "assign_later";
}

interface ApiResponse<T> {
  data: T;
  downloaded?: any[];
  message?: string;
  status?: number;
  schedules?: any[];
  tdsList?: any[];
  
}


export default function RentalSchedules({ onViewSchedule, tdsRateList }: RentalSchedulesProps) {
  const { workflow } = useCustomerWorkflow();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [lines, setLines] = useState<ScheduleLine[] | null>(null);
  const [upfront, setUpfront] = useState<UpfrontCharges | null>(null);
  const [meta, setMeta] = useState<{ config: ScheduleConfig; assets: Asset[] } | null>(null);
  const [termSheetList, setTermSheetList] = useState<any[]>([]);
  const [purchaseOrderAssetList, setPurchaseOrderAssetList] = useState<any[]>([]);
  const { customerId } = useParams<{ customerId: string }>();
  const [termSheetId, setTermSheetId] = useState<string>("");
  const [scheduleCreated, setScheduleCreated] = useState(false);
  const [schedulePendingDelete, setSchedulePendingDelete] = useState<Schedule | null>(null);
  const [isDeletingSchedule, setIsDeletingSchedule] = useState(false);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(!!customerId);

  useEffect(() => {
    getRentalSchedules();
    getTermSheetList();
    getPurchaseOrderAssetList();
  }, [customerId]);

  const handleViewSchedule = (schedule: any) => {
    onViewSchedule(schedule);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft": return "secondary";
      case "Submitted": return "warning";
      case "Approved": return "warning";
      case "In Signing": return "info";
      case "Assigned": return "success";
      case "Matured": return "destructive";
      case "Active": return "success";
      default: return "secondary";
    }
  };

  const getTermSheetList = async () => {
    if (!customerId) return;
    const response = await apiService.get<ApiResponse<any>>(`/customers/get-term-sheet/${customerId}`);
    const data = response.downloaded;
    // Filter to only include term sheets where isRentalScheduleCreated = 0
    // const filteredData = (data ?? []).filter((item: any) => item.isRentalScheduleCreated === 0);
    setTermSheetList(Array.isArray(data) ? data : []);
  };

  const getPurchaseOrderAssetList = async () => {
    if (!customerId) return;

    const response = await apiService.get<ApiResponse<any>>(`/purchase-order/get-purchase-order-asset-list/${customerId}`);
    const data = response.data;

    // Transform poNumber to PO-{last 4 digits}
    const transformedData = data.map((item: Asset) => ({
      ...item,
      poNumber: item.poNumber ? `PO-${item.poNumber.slice(-4)}` : item.poNumber
    }));

    setPurchaseOrderAssetList(transformedData);

  }

  const getRentalSchedules = async () => {
    if (!customerId) {
      setSchedules([]);
      return;
    }
    setIsLoadingSchedules(true);
    try {
      const response = await apiService.get<ApiResponse<Schedule[]>>(
        `/rental-schedule/get-rental-schedules?customerId=${customerId}`
      );
      const data = response.schedules;
      setSchedules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch rental schedules:", error);
      toast.error("Failed to load rental schedules");
      setSchedules([]);
    } finally {
      setIsLoadingSchedules(false);
    }
  };

  const confirmDeleteRentalSchedule = async () => {
    if (!schedulePendingDelete) return;
    if (schedulePendingDelete.noa !== null) {
      toast.error("Cannot delete: this schedule has a NOA linked");
      setSchedulePendingDelete(null);
      return;
    }
    if (isAssignLaterSchedule(schedulePendingDelete)) {
      toast.error("Cannot delete: self-funded schedule (assign later)");
      setSchedulePendingDelete(null);
      return;
    }
    try {
      setIsDeletingSchedule(true);
      await apiService.post(`/rental-schedule/delete-rental-schedule/${schedulePendingDelete.id}`, {});
      toast.success("Rental schedule deleted");
      setSchedulePendingDelete(null);
      await getRentalSchedules();
      await getTermSheetList();
      await getPurchaseOrderAssetList();
    } catch (error) {
      console.error("Failed to delete rental schedule:", error);
      toast.error("Failed to delete rental schedule");
    } finally {
      setIsDeletingSchedule(false);
    }
  };


  const addRentalSchedule = async () => {
    if (lines && upfront && meta && customerId) {
      try {
        const tdsRate =
          meta.config?.TDS_rate ??
          meta.config?.tdsRatePercent ??
          2;

        const { totalBaseAssetValue, totalAssetGst, totalAssetValue } =
          computeAssetValueTotals(meta.assets);

        const newSchedule = {
          id: `RS-${Date.now()}`,
          customerId: customerId,
          termSheetId: termSheetId,
          lines: lines,
          upfront: upfront,
          config: {
            ...meta.config,
            tdsRatePercent: tdsRate,
            TDS_rate: tdsRate,
          },
          assets: meta.assets,
          TDS_rate: tdsRate,
          totalBaseAssetValue,
          totalAssetGst,
          totalAssetValue,
        };


        const response = await apiService.post<ApiResponse<any>>(
          `/rental-schedule/create-rental-schedule`,
          newSchedule
        );

        if (response.status === 200 || response.data) {
          toast.success("Schedule created successfully");
          setScheduleCreated(true); // Mark that schedule was created successfully
          setPreviewOpen(false);
          setDrawerOpen(false);
          // Refresh the schedules list after creating a new one
          getRentalSchedules();
          getTermSheetList();
          getPurchaseOrderAssetList();
        }
      } catch (error) {
        console.error("Failed to create schedule:", error);
        toast.error("Failed to create schedule");
        setPreviewOpen(false);
        setDrawerOpen(false);
      }
    } else {
      setPreviewOpen(false);
      setDrawerOpen(false);
    }
  }

  const formatScheduleNumber = (id: string | number | undefined): string => {
    if (!id) return "";
    const num = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(num)) return String(id);
    return num < 10 ? `0${num}` : String(num);
  };

    const getDisplayStatus = (scheduleData: Schedule & {
      assignMode?: string;
      lenderAssignment?: string;
      assignmentMode?: string;
    }) => {
    const today = new Date();
    const lastDate = scheduleData?.config?.buy_back_date
      ? new Date(scheduleData.config.buy_back_date)
      : null;
    const status = scheduleData.status?.toLowerCase();

    if (status === "draft") {
      return "Draft";
    }

    const noaApproved = scheduleData?.noa?.status?.toLowerCase() === "approved";
    const assignLater = isAssignLaterSchedule(scheduleData);

    // Approved + (NoA approved or self-funded assign_later): Active / Matured by buy-back date
    if (status === "approved" && (noaApproved || assignLater)) {
      if (lastDate) {
        if (today < lastDate) {
          return "Active";
        }
        return "Matured";
      }
    }

    return status
      ? status.charAt(0).toUpperCase() + status.slice(1)
      : "—";
};

  // const displayStatus = getDisplayStatus(schedules?.[0] ?? {});

  return (
    <div className="space-y-6">
      {/* <Card className="shadow-[var(--shadow-elevated)]">
        <CardHeader>
          <CardTitle>Rental Schedules</CardTitle>
          <CardDescription>Base Asset Value: ₹{baseAssetValue.toLocaleString("en-IN")}. Generate schedules with advance billing and GST by HSN.</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          
        </CardContent>
      </Card> */}

      <div className="flex justify-end gap-2 mb-4">
        <Button 
        tooltip={workflow.createRentalSchedule.reason}
        disabled={!workflow.createRentalSchedule.enabled}
        onClick={() => setDrawerOpen(true)}>
          Generate Rental Schedule
          </Button>
        {/* <Button variant="secondary">Visualise Rentals</Button> */}
      </div>

      <div className="relative">
        <Card className={isDeletingSchedule || isLoadingSchedules ? "min-h-[200px]" : undefined}>
        <CardHeader>
          <CardTitle>Recent Schedules</CardTitle>
          <CardDescription>Latest generated schedules appear here.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-center">Rental Schedule ID.</TableHead>
                <TableHead className="text-center">RS NO</TableHead>
                <TableHead className="text-center">Frequency</TableHead>
                <TableHead className="text-center">Tenure (Month)</TableHead>
                <TableHead className="text-center">Start</TableHead>
                <TableHead className="text-center">Maturity Date</TableHead>
                <TableHead className="text-center">Lender Name</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!isLoadingSchedules &&
              schedules.map((schedule) => {
                const canDeleteSchedule =
                  schedule.noa == null && !isAssignLaterSchedule(schedule);
                return (
                <TableRow key={schedule.id}>
                  {/* ...existing code... */}
                  <TableCell className="text-xs text-muted-foreground text-center">{schedule.rentalScheduleCode}</TableCell>
                  <TableCell className="text-center">{formatScheduleNumber(schedule.displayId) ?? '-'}</TableCell>
                  <TableCell className="text-center">{schedule.config.frequency?.charAt(0).toUpperCase() + schedule.config.frequency?.slice(1)}</TableCell>
                  <TableCell className="text-center">{schedule.config.tenureMonths}</TableCell>
                  <TableCell className="text-center">
                    {formatDisplayDate(schedule.lines?.[0]?.dueDate)}
                  </TableCell>
                  <TableCell className="text-center">
                    {formatDisplayDate(schedule.lines?.[schedule.lines.length - 1]?.toDate)}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      {isAssignLaterSchedule(schedule) && !schedule.noa ? (
                        <Avatar className="rounded-lg" style={{ height: "30px", width: "45px" }}>
                          <AvatarImage
                            src={astrafinLogo}
                            alt="Astrafin"
                            className="object-contain"
                          />
                        </Avatar>
                      ) : schedule.noa?.lenderAvatar ? (
                        <Avatar className="rounded-lg" style={{ height: "30px", width: "45px" }}>
                          <AvatarImage
                            src={
                              schedule.noa.lenderAvatar.startsWith("http")
                                ? schedule.noa.lenderAvatar
                                : `${import.meta.env.VITE_API_BASE_URL}/${schedule.noa.lenderAvatar}`
                            }
                            alt={schedule.noa?.lenderName}
                            className="object-contain"
                          />
                        </Avatar>
                      ) : null}
                      <div className="font-medium">
                        {isAssignLaterSchedule(schedule) && !schedule.noa
                          ? "Astrafin"
                          : schedule.noa?.lenderName ?? "—"}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={getStatusColor(getDisplayStatus(schedule))}>
                      {getDisplayStatus(schedule)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex gap-1 justify-center items-center">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        title="View"
                        onClick={() => handleViewSchedule(schedule)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={!canDeleteSchedule}
                        className={
                          canDeleteSchedule
                            ? "h-8 w-8 shrink-0 text-destructive hover:text-destructive hover:bg-red-50"
                            : "h-8 w-8 shrink-0 cursor-not-allowed opacity-40 text-muted-foreground hover:bg-transparent hover:text-muted-foreground"
                        }
                        title={
                          canDeleteSchedule
                            ? "Delete"
                            : schedule.noa != null
                              ? "Cannot delete: this schedule has a NOA linked"
                              : "Cannot delete: self-funded schedule (assign later)"
                        }
                        onClick={() => {
                          if (!canDeleteSchedule) return;
                          setSchedulePendingDelete(schedule);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
              {!isLoadingSchedules && schedules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                    No rental schedules found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
        </Card>
        {(isLoadingSchedules || isDeletingSchedule) && (
          <div
            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-lg bg-white/60 backdrop-blur-[1px]"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium text-foreground/80">
              {isDeletingSchedule ? "Deleting and refreshing…" : "Loading schedules…"}
            </p>
          </div>
        )}
      </div>

      <ScheduleGeneratorDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          // Reset the flag when drawer closes
          if (!open && scheduleCreated) {
            setScheduleCreated(false);
          }
        }}
        assets={purchaseOrderAssetList}
        termSheetList={termSheetList}
        onPreview={(l, u, m, termSheetId) => {
          setLines(l);
          setUpfront(u);
          setMeta(m);
          setTermSheetId(termSheetId);
          setPreviewOpen(true);
        }}
        onCreate={scheduleCreated ? (() => { }) : undefined}
        tdsRateList={tdsRateList}
      />

      <SchedulePreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        lines={lines ?? []}
        upfront={upfront}
        config={meta?.config}
        assets={meta?.assets}
        onConfirm={addRentalSchedule}
        showConfirmActions
      />

      <AlertDialog
        open={!!schedulePendingDelete}
        onOpenChange={(open) => {
          if (!open && !isDeletingSchedule) setSchedulePendingDelete(null);
        }}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this rental schedule?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>This permanently removes the schedule from the list. This cannot be undone.</p>
                {(schedulePendingDelete?.rentalScheduleCode != null &&
                  schedulePendingDelete.rentalScheduleCode !== "") ||
                schedulePendingDelete?.displayId != null ? (
                  <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2 text-left font-normal text-slate-800">
                    {schedulePendingDelete?.rentalScheduleCode != null &&
                      schedulePendingDelete.rentalScheduleCode !== "" && (
                        <div className="font-mono text-xs">
                          <span className="text-slate-500">Rental schedule ID · </span>
                          {schedulePendingDelete.rentalScheduleCode}
                        </div>
                      )}
                    {schedulePendingDelete?.displayId != null &&
                      schedulePendingDelete.displayId !== "" && (
                        <div className="mt-1 font-mono text-xs">
                          <span className="text-slate-500">RS NO · </span>
                          {formatScheduleNumber(schedulePendingDelete.displayId) || "—"}
                        </div>
                      )}
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingSchedule}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="border-transparent bg-red-600 text-white hover:bg-red-700 hover:text-white focus-visible:ring-red-600"
              disabled={isDeletingSchedule}
              onClick={() => void confirmDeleteRentalSchedule()}
            >
              {isDeletingSchedule ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
